import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTVShowsWithPeople, usePeopleDataState } from '../hooks/useContentQueries';
import { ContentCard, PersonSummaryWithPhoto } from '../types';
import FilterPanel from '../components/FilterPanel';
import ContentGrid from '../components/ContentGrid';
import { navigateToContent, navigateToPerson, hasPersonDetailPage } from '../utils/navigation';

const TVShowsPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedGenre, setSelectedGenre] = useState<string | undefined>();
  const [selectedYear, setSelectedYear] = useState<number | undefined>();
  const [page, setPage] = useState(1);
  const [allTVShows, setAllTVShows] = useState<ContentCard[]>([]);

  const { data: tvShowsData, isLoading, error } = useTVShowsWithPeople(page, 20, {
    genre: selectedGenre,
    year: selectedYear,
  });

  // Track people data loading state for progressive loading indicators
  const peopleDataState = usePeopleDataState(allTVShows);

  // Update TV shows list when data changes
  React.useEffect(() => {
    if (tvShowsData?.data) {
      if (page === 1) {
        setAllTVShows(tvShowsData.data);
      } else {
        setAllTVShows(prev => [...prev, ...tvShowsData.data]);
      }
    }
  }, [tvShowsData, page]);

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1);
    setAllTVShows([]);
  }, [selectedGenre, selectedYear]);

  const handleContentClick = (content: ContentCard) => {
    navigateToContent(navigate, content);
  };

  const handlePersonClick = (person: PersonSummaryWithPhoto) => {
    // Check if person has a detail page available
    if (hasPersonDetailPage(person)) {
      navigateToPerson(navigate, person);
    }
  };

  const handleLoadMore = useCallback(() => {
    if (tvShowsData?.pagination?.hasMore && !isLoading) {
      setPage(prev => prev + 1);
    }
  }, [tvShowsData?.pagination?.hasMore, isLoading]);

  const handleGenreChange = (genre: string | undefined) => {
    setSelectedGenre(genre);
  };

  const handleYearChange = (year: number | undefined) => {
    setSelectedYear(year);
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Shows
          </h1>
          <p className="text-gray-400">
            Explore the best television series
          </p>
        </div>

        {/* Filters */}
        <FilterPanel
          selectedGenre={selectedGenre}
          selectedYear={selectedYear}
          onGenreChange={handleGenreChange}
          onYearChange={handleYearChange}
          className="mb-8"
        />

        {/* Results Count */}
        {tvShowsData?.pagination && (
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="text-gray-400">
              {tvShowsData.pagination.total > 0 ? (
                <span>
                  Showing {allTVShows.length} of {tvShowsData.pagination.total} shows
                </span>
              ) : (
                <span>No shows found</span>
              )}
            </div>
            
            {/* People Data Loading Indicator */}
            {allTVShows.length > 0 && !peopleDataState.isComplete && (
              <div className="flex items-center text-sm text-blue-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400 mr-2"></div>
                <span>Loading cast photos... ({Math.round(peopleDataState.loadingProgress)}%)</span>
              </div>
            )}
            
            {/* People Data Complete Indicator */}
            {allTVShows.length > 0 && peopleDataState.isComplete && peopleDataState.stats.withPeople > 0 && (
              <div className="flex items-center text-sm text-green-400">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Cast photos loaded ({peopleDataState.stats.withPeople} shows)</span>
              </div>
            )}
          </div>
        )}

        {/* Content Grid */}
        {error ? (
          <div className="text-center py-16">
            <div className="text-red-400 text-lg mb-4">
              Failed to load shows
            </div>
            <button 
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        ) : (
          <ContentGrid
            content={allTVShows}
            onContentClick={handleContentClick}
            onPersonClick={handlePersonClick}
            onLoadMore={handleLoadMore}
            hasMore={tvShowsData?.pagination?.hasMore}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default TVShowsPage;